import { Check } from "lucide-react"
import type { PromptIndex, PromptSummary } from "@/lib/prompt-audit-api"

const MATRIX_KINDS = new Set(["fragment", "document", "template"])
const HOST_KINDS = new Set(["surface", "template"])

function Section({ title, children, note }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      {note && <p className="mt-1 max-w-[68ch] text-sm text-muted-foreground">{note}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function EntryLink({ entry, onSelect }: { entry: PromptSummary; onSelect: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onSelect(entry.id)} className="text-left text-sm font-medium text-primary hover:underline">
      {entry.title}
    </button>
  )
}

export function AuditOverview({ index, onSelect }: { index: PromptIndex; onSelect: (id: string) => void }) {
  const entries = index.entries
  const surfaces = entries.filter((entry) => entry.kind === "surface" || entry.kind === "tools")
  const drifted = entries.filter((entry) => entry.drift)
  const failed = entries.filter((entry) => entry.error)
  const openComments = entries.reduce((sum, entry) => sum + entry.open_comments, 0)
  const hosts = entries.filter((entry) => HOST_KINDS.has(entry.kind) && entries.some((piece) => piece.included_in.includes(entry.id)))
  const shared = entries
    .filter((entry) => MATRIX_KINDS.has(entry.kind) && entry.included_in.length > 0)
    .sort((a, b) => b.included_in.length - a.included_in.length)
  const sha = index.deploy.commit_sha

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-10">
      <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Prompt audit</p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight">Every prompt the agents run</h1>
      <p className="mt-2 max-w-[68ch] text-sm text-muted-foreground">
        Built live from the code the agents run. To change a prompt, change the code. Commit <span className="font-mono text-xs">{sha ? sha.slice(0, 12) : "unknown"}</span> at{" "}
        {new Date(index.deploy.assembled_at).toLocaleString()}.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Prompts and palettes" value={surfaces.length} />
        <Metric label="Everything audited" value={entries.length} />
        <Metric label="Live copies that differ from the repo" value={drifted.length} />
        <Metric label="Open comments" value={index.comments_available ? openComments : "Not set up"} />
      </div>

      {failed.length > 0 && (
        <Section title="Could not assemble" note="These builders raised in this process. The page shows everything else.">
          <ul className="space-y-1">{failed.map((entry) => <li key={entry.id}><EntryLink entry={entry} onSelect={onSelect} /> <span className="text-xs text-muted-foreground">({entry.error})</span></li>)}</ul>
        </Section>
      )}

      <Section
        title="Live copy against repo copy"
        note="Documents the agents read from the configuration bucket. A difference means the model reads text that is not in git."
      >
        {drifted.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="size-4 text-emerald-600" aria-hidden="true" />Every loaded document matches the repo.</p>
        ) : (
          <ul className="space-y-1">
            {drifted.map((entry) => (
              <li key={entry.id}>
                <EntryLink entry={entry} onSelect={onSelect} />
                {entry.live_state === "absent" && <span className="ml-2 text-xs text-muted-foreground">no live copy</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Where shared text appears"
        note="Each row is a fragment or document, each column a prompt that contains it word for word."
      >
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th scope="col" className="sticky left-0 bg-muted/40 px-3 py-2 text-left font-medium">Text</th>
                {hosts.map((host) => (
                  <th key={host.id} scope="col" className="px-2 py-2 text-left align-bottom text-xs font-medium whitespace-nowrap text-muted-foreground">
                    <button type="button" onClick={() => onSelect(host.id)} className="hover:text-foreground">{host.title}</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shared.map((entry) => (
                <tr key={entry.id} className="border-b last:border-0">
                  <th scope="row" className="sticky left-0 bg-background px-3 py-1.5 text-left font-normal whitespace-nowrap">
                    <EntryLink entry={entry} onSelect={onSelect} />
                  </th>
                  {hosts.map((host) => (
                    <td key={host.id} className="px-2 py-1.5 text-center">
                      {entry.included_in.includes(host.id) ? (
                        <Check className="mx-auto size-4 text-emerald-600" aria-label={`In ${host.title}`} />
                      ) : (
                        <span className="sr-only">Not in {host.title}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  )
}
